import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import Modal from 'react-bootstrap/Modal';
import Table from 'react-bootstrap/Table';
import Spinner from 'react-bootstrap/Spinner';
import Button from 'react-bootstrap/Button';
import { BOOKMAKER_ALIASES } from '../utils/constants';

// Create reverse alias map (caesars -> williamhill_us)
const REVERSE_ALIASES = Object.entries(BOOKMAKER_ALIASES).reduce((acc, [key, value]) => {
  acc[value] = key;
  return acc;
}, {});

const H2HGameModal = ({
  show,
  onHide,
  game,
  gameId,
  sport = 'nba',
  getTeamLogoUrl,
  getBookLogoUrl,
  bookmakers = [],
  onOddsClick,
  onRemoveBet,
  betslipItems = []
}) => {
  const [marketData, setMarketData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showSpreads, setShowSpreads] = useState(true);
  const [showTotals, setShowTotals] = useState(false);
  const [showAlts, setShowAlts] = useState(true);
  const [showSingleBooks, setShowSingleBooks] = useState(false);
  const [showEdgesOnly, setShowEdgesOnly] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  // Mobile bottom-sheet state for odds detail + Add/Remove to Betslip.
  // Separate from the ref-based desktop tooltip because mobile needs React
  // state to re-render the sheet (and because the sheet renders inside the
  // Modal body, not as a floating positioned tooltip).
  const [mobileSheet, setMobileSheet] = useState(null);

  // =============================================================================
  // PERFORMANCE OPTIMIZATION: Use ref for tooltip instead of state
  // This prevents full component re-renders on every hover/click
  // To revert: change back to useState and update handleOddsHover/hideTooltip
  // =============================================================================
  const tooltipRef = useRef(null);
  const tooltipDataRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch game data when modal opens
  useEffect(() => {
    if (show && gameId) {
      fetchGameData();
    } else if (!show) {
      setMarketData(null);
      setError(null);
    }
  }, [show, gameId]);

  const fetchGameData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Use sport-specific API endpoint
      const sportPath = sport.toLowerCase();
      const response = await fetch(`/api/games/${sportPath}/${gameId}/odds/h2h?all=true`);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      const data = await response.json();
      setMarketData(data);
    } catch (err) {
      console.error('Error fetching game data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Close tooltip when clicking outside (mobile only) - using ref-based approach
  useEffect(() => {
    if (isMobile) {
      const handleClickOutside = (e) => {
        // Don't hide if clicking on the tooltip itself
        if (tooltipRef.current && !tooltipRef.current.contains(e.target)) {
          hideTooltip();
        }
      };
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [isMobile]);

  // Helper to hide tooltip without causing re-render
  const hideTooltip = useCallback(() => {
    if (tooltipRef.current) {
      tooltipRef.current.style.display = 'none';
    }
    tooltipDataRef.current = null;
  }, []);

  // Helper to format odds with +/- sign
  const formatOdds = (odds) => {
    if (odds === null || odds === undefined || odds === '') return '-';
    const numOdds = parseInt(odds, 10);
    if (isNaN(numOdds)) return '-';
    return numOdds > 0 ? `+${numOdds}` : `${numOdds}`;
  };

  // Calculate implied probability from American odds
  const calculateImpliedProbability = (odds) => {
    const numericOdds = parseInt(odds, 10);
    if (isNaN(numericOdds)) return null;
    if (numericOdds > 0) {
      return (100 / (numericOdds + 100)) * 100;
    } else {
      return (Math.abs(numericOdds) / (Math.abs(numericOdds) + 100)) * 100;
    }
  };

  // Handle odds cell hover/click to show tooltip - using refs to avoid re-renders
  const handleOddsHover = useCallback((event, odds, book, bestBook, bestBookDiff) => {
    if (!odds || odds === '-') return;
    if (!tooltipRef.current) return;

    const impliedProb = calculateImpliedProbability(odds);
    if (!impliedProb) return;

    // Only show edge for the best book (when there's a single best book, not "multi")
    const bookAlias = BOOKMAKER_ALIASES[book] || book;
    const reverseAlias = REVERSE_ALIASES[book] || book;
    const isBestBook = bestBook && bestBook !== 'multi' &&
      (book === bestBook || bookAlias === bestBook || reverseAlias === bestBook);
    const edge = (isBestBook && bestBookDiff) ? bestBookDiff * 100 : 0;

    let x, y;
    if (isMobile) {
      x = window.innerWidth / 2 - 100;
      y = Math.min(event.clientY, window.innerHeight - 150);
    } else {
      x = event.clientX;
      y = event.clientY;
    }

    // Store data in ref
    tooltipDataRef.current = {
      odds,
      book,
      impliedProb: impliedProb.toFixed(2),
      edge: edge.toFixed(2)
    };

    // Update tooltip DOM directly without causing re-render
    const tooltip = tooltipRef.current;
    tooltip.style.display = 'block';
    tooltip.style.left = `${x + 10}px`;
    tooltip.style.top = `${y + 10}px`;

    // Update tooltip content
    tooltip.innerHTML = `
      <div style="font-weight: bold; margin-bottom: 4px;">${book}</div>
      <div>Odds: ${odds > 0 ? '+' : ''}${odds}</div>
      <div>Implied: ${impliedProb.toFixed(2)}%</div>
      ${edge > 0 ? `<div style="color: #4ade80;">Edge: ${edge.toFixed(2)}%</div>` : ''}
    `;
  }, [isMobile]);

  // Shared cell-activation handler. Routes to one of three behaviors:
  //   - no betslip wired (legacy callers): keep the ref-based tooltip only.
  //   - desktop + betslip wired: add straight to the betslip, keep the hover
  //     tooltip as the detail popover.
  //   - mobile + betslip wired: open a bottom-sheet with Add/Remove.
  // betMeta carries the full bet shape so the mobile sheet's buttons can call
  // onOddsClick / onRemoveBet without the caller repeating the wiring.
  const activateCell = useCallback((event, { odds, book, bestBook, bestBookDiff, betMeta }) => {
    if (!odds || odds === '-') return;

    if (!onOddsClick) {
      handleOddsHover(event, odds, book, bestBook, bestBookDiff);
      return;
    }

    if (!isMobile) {
      onOddsClick(
        betMeta.gameId, betMeta.playerId, betMeta.playerName, betMeta.marketKey,
        book, odds, betMeta.awayTeam, betMeta.homeTeam,
        betMeta.playerTeam, betMeta.playerPosition, betMeta.line, betMeta.side
      );
      return;
    }

    const impliedProb = calculateImpliedProbability(odds);
    const bookAlias = BOOKMAKER_ALIASES[book] || book;
    const reverseAlias = REVERSE_ALIASES[book] || book;
    const isBestBook = bestBook && bestBook !== 'multi' &&
      (book === bestBook || bookAlias === bestBook || reverseAlias === bestBook);
    const edge = (isBestBook && bestBookDiff) ? bestBookDiff * 100 : 0;

    setMobileSheet({
      odds,
      book,
      point: betMeta.pointLabel,
      impliedProb: impliedProb ? impliedProb.toFixed(2) : '0.00',
      edge: edge.toFixed(2),
      meta: betMeta,
    });
  }, [isMobile, onOddsClick, handleOddsHover]);

  // Calculate American odds diff between two odds values
  const calculateAmericanOddsDiff = (bestOdds, secondOdds) => {
    if (bestOdds === null || bestOdds === undefined || secondOdds === null || secondOdds === undefined) {
      return 0;
    }

    // Both same sign
    if ((bestOdds > 0 && secondOdds > 0) || (bestOdds < 0 && secondOdds < 0)) {
      return Math.abs((bestOdds - secondOdds) / secondOdds);
    }
    // Best positive, second negative (crossing even money)
    else if (bestOdds > 0 && secondOdds < 0) {
      const bestDistance = bestOdds - 100;
      const secondDistance = secondOdds + 100;
      return Math.abs((bestDistance - secondDistance) / secondOdds);
    }
    // Best negative, second positive (crossing even money)
    else {
      const bestDistance = bestOdds + 100;
      const secondDistance = secondOdds - 100;
      return Math.abs((bestDistance - secondDistance) / secondOdds);
    }
  };

  // Get tier style based on diff value
  const getTierStyle = (diffValue) => {
    if (diffValue >= 0.25) {
      return { backgroundColor: 'green', color: 'yellow' };
    } else if (diffValue >= 0.15) {
      return { backgroundColor: '#F59E0B', color: '#1E40AF' };
    } else if (diffValue >= 0.05) {
      return { backgroundColor: '#3B82F6', color: 'white' };
    }
    return null;
  };

  // Get the cell style for best odds highlighting with edge tier colors
  // Uses the same tier system as the main data tables
  const getCellStyle = (odds, allOddsForSide, bestBookDiff = null, bestBook = null, currentBook = null, allOddsWithBooks = null) => {
    if (!odds || !allOddsForSide || allOddsForSide.length === 0) return {};

    const validOdds = allOddsForSide.filter(o => o !== null && o !== undefined);
    if (validOdds.length <= 1) return {};

    const bestOdds = Math.max(...validOdds);
    const booksWithBest = validOdds.filter(o => o === bestOdds).length;

    // Check if bet365 is the best book (only for NCAAB)
    const isBet365Best = bestBook === 'bet365' && sport === 'ncaab';

    // Highlight if this is the best price
    if (odds === bestOdds) {
      const style = { fontWeight: 'bold', borderRadius: '3px' };

      // If only 1 book has the best price, use tier colors based on edge
      if (booksWithBest === 1 && bestBookDiff !== null && bestBookDiff !== undefined) {
        const diffValue = Math.abs(bestBookDiff);
        const tierStyle = getTierStyle(diffValue);
        if (tierStyle) {
          Object.assign(style, tierStyle);
        } else {
          // Small edge - just text highlight, no background
          Object.assign(style, { color: '#F59E0B' });
        }
      } else {
        // Multiple books have best price OR no diff data - just text highlight
        Object.assign(style, { color: '#F59E0B' });
      }

      return style;
    }

    // NCAAB special case: If bet365 is the best book, also apply tier coloring to next best book
    if (isBet365Best && allOddsWithBooks && currentBook) {
      // Get odds from books OTHER than bet365, sorted by odds descending
      const otherBooksOdds = [...allOddsWithBooks]
        .filter(o => o.odds !== null && o.odds !== undefined && o.book !== 'bet365')
        .sort((a, b) => b.odds - a.odds);

      if (otherBooksOdds.length >= 1) {
        const nextBestBook = otherBooksOdds[0].book;
        const nextBestOdds = otherBooksOdds[0].odds;

        // Check if current book is the next best book
        if (currentBook === nextBestBook) {
          // Calculate diff between 2nd and 3rd best (need at least 2 other books)
          if (otherBooksOdds.length >= 2) {
            const thirdBestOdds = otherBooksOdds[1].odds;
            const diff = calculateAmericanOddsDiff(nextBestOdds, thirdBestOdds);
            const tierStyle = getTierStyle(diff);
            if (tierStyle) {
              return { fontWeight: 'bold', borderRadius: '3px', ...tierStyle };
            }
          }
        }
      }
    }

    return {};
  };

  // Helper to check if a record is a main line for at least one book
  // Uses the _isMain flag pattern: spreads_minus_home_fanduel_isMain, totals_over_fanduel_isMain, etc.
  // Falls back to checking _isAlternate === false for backward compatibility
  const isMainLine = (record, marketType) => {
    // First check for new _isMain format
    for (const key of Object.keys(record)) {
      if (key.startsWith(`${marketType}_`) && key.endsWith('_isMain') && record[key] === true) {
        return true;
      }
    }
    // Fallback: check for old _isAlternate === false format
    // Check both patterns and per-side patterns
    for (const book of bookmakers) {
      const checkBook = (b) => {
        // Pattern 1: marketType_book_isAlternate
        if (record[`${marketType}_${b}_isAlternate`] === false) return true;
        // Check if any per-side isAlternate is false for this book
        return Object.keys(record).some(key =>
          key.startsWith(`${marketType}_`) &&
          key.includes(`_${b}`) &&
          key.endsWith('_isAlternate') &&
          record[key] === false
        );
      };
      if (checkBook(book)) return true;
      const aliasBook = REVERSE_ALIASES[book];
      if (aliasBook && checkBook(aliasBook)) return true;
    }
    return false;
  };

  // Process market data into structured format
  const processMarketData = () => {
    if (!marketData?.markets?.[0]) return { h2h: [], spreads: [], totals: [] };

    const container = marketData.markets[0];
    const isSoccer = sport === 'soccer';

    let h2h = container.h2h || [];
    let spreads = container.spreads || [];
    let totals = container.totals || [];

    // Non-soccer sports have no draw — drop h2h_3_way records so only h2h_binary is shown
    if (!isSoccer) {
      h2h = h2h.filter(record => record.tierKey !== 'h2h_3_way');
    }

    // For soccer, merge h2h_3_way and h2h_binary data
    // h2h_3_way is the truth, but add books from h2h_binary that aren't in h2h_3_way
    if (isSoccer && h2h.length > 0) {
      const h2h3WayRecord = h2h.find(record => record.tierKey === 'h2h_3_way');
      const h2hBinaryRecord = h2h.find(record => record.tierKey === 'h2h_binary');

      if (h2h3WayRecord || h2hBinaryRecord) {
        const mergedRecord = { ...(h2h3WayRecord || h2hBinaryRecord) };

        if (h2h3WayRecord && h2hBinaryRecord) {
          // Find books in h2h_binary that aren't in h2h_3_way and add them
          const sides = ['away', 'home', 'draw'];
          Object.keys(h2hBinaryRecord).forEach(key => {
            const match = key.match(/^h2h_([a-z0-9_]+)_(away|home|draw)$/);
            if (match && !key.includes('bestBook') && !key.includes('nextBook') && !key.includes('isAlternate') && !key.includes('isAvailable')) {
              const bookName = match[1];
              const existsIn3Way = sides.some(side =>
                h2h3WayRecord[`h2h_${bookName}_${side}`] !== undefined
              );
              if (!existsIn3Way) {
                sides.forEach(side => {
                  const oddsKey = `h2h_${bookName}_${side}`;
                  const availKey = `h2h_${bookName}_${side}_isAvailable`;
                  if (h2hBinaryRecord[oddsKey] !== undefined) {
                    mergedRecord[oddsKey] = h2hBinaryRecord[oddsKey];
                  }
                  if (h2hBinaryRecord[availKey] !== undefined) {
                    mergedRecord[availKey] = h2hBinaryRecord[availKey];
                  }
                });
              }
            }
          });
        }

        h2h = [mergedRecord];
      }
    }

    // Filter out alternates if showAlts is disabled
    if (!showAlts) {
      h2h = h2h.filter(record => isMainLine(record, 'h2h'));
      spreads = spreads.filter(record => isMainLine(record, 'spreads'));
      totals = totals.filter(record => isMainLine(record, 'totals'));
    }

    return { h2h, spreads, totals };
  };

  // Render a market table (h2h, spreads, or totals)
  const renderMarketTable = (records, marketType, title) => {
    if (!records || records.length === 0) return null;

    // For spreads, we need to check for all 4 combinations per point:
    // plus_away, minus_away, plus_home, minus_home
    // For h2h and totals, use the standard sides
    // Soccer h2h includes 'draw' option
    const isSoccer = sport === 'soccer';
    const sides = marketType === 'totals'
      ? ['over', 'under']
      : (marketType === 'h2h' && isSoccer)
        ? ['away', 'draw', 'home']
        : ['away', 'home'];
    const sideLabels = marketType === 'totals'
      ? { over: 'Over', under: 'Under' }
      : { away: game?.away || 'Away', home: game?.home || 'Home', draw: 'Draw' };

    // For spreads, define all possible combinations
    const spreadCombinations = [
      { side: 'away', sign: 'plus', label: game?.away || 'Away', displaySign: '+' },
      { side: 'away', sign: 'minus', label: game?.away || 'Away', displaySign: '-' },
      { side: 'home', sign: 'plus', label: game?.home || 'Home', displaySign: '+' },
      { side: 'home', sign: 'minus', label: game?.home || 'Home', displaySign: '-' }
    ];

    // Group records by point value
    const pointGroups = {};
    records.forEach(record => {
      const point = record.point ?? 'main';
      if (!pointGroups[point]) {
        pointGroups[point] = record;
      }
    });

    // Sort points numerically
    const sortedPoints = Object.keys(pointGroups).sort((a, b) => {
      if (a === 'main') return -1;
      if (b === 'main') return 1;
      return parseFloat(a) - parseFloat(b);
    });

    // Helper to render a header row (for repeating headers every N rows)
    const renderHeaderRow = (keyPrefix) => (
      <tr key={`${keyPrefix}-header`} style={{ backgroundColor: 'var(--bs-table-bg, #212529)' }}>
        <th style={{ width: '80px', minWidth: '80px' }}>
          {marketType === 'totals' ? 'Total' : 'Line'}
        </th>
        {marketType === 'spreads' && (
          <th style={{ width: '40px', minWidth: '40px' }}>+/-</th>
        )}
        <th style={{ width: '60px', minWidth: '60px' }}>Side</th>
        {bookmakers.map(book => (
          <th key={`${keyPrefix}-${book}`} style={{ width: '70px', minWidth: '70px' }}>
            <img
              src={getBookLogoUrl(book)}
              alt={book}
              style={{ height: '18px' }}
              title={book}
            />
          </th>
        ))}
      </tr>
    );

    // Track row count for inserting repeated headers
    let rowCount = 0;
    const HEADER_INTERVAL = 20;

    return (
      <div className="mb-4">
        <h6 style={{ color: 'rgb(245, 158, 11)', fontWeight: 'bold', marginBottom: '10px' }}>
          {title}
        </h6>
        <div style={{ overflowX: 'auto' }}>
          <Table bordered size="sm" className="text-center" style={{ fontSize: '0.85rem' }}>
            <thead>
              {renderHeaderRow('thead')}
            </thead>
            <tbody>
              {sortedPoints.map(point => {
                const record = pointGroups[point];
                const pointDisplay = point === 'main' ? '-' : point;

                // For spreads, iterate over all 4 combinations
                if (marketType === 'spreads') {
                  // Helper to get odds for a specific combination (sign + side)
                  const getSpreadOddsForBook = (book, sign, side) => {
                    const key = `spreads_${sign}_${side}_${book}`;
                    const isMainKey = `${key}_isMain`;
                    const isAltKey = `${key}_isAlternate`;
                    const isAvailableKey = `${key}_isAvailable`;

                    let value = record[key];
                    let isMain = record[isMainKey] === true;
                    let isAlt = record[isAltKey] === true;
                    let isAvailable = record[isAvailableKey] !== false;

                    if (value === undefined) {
                      // Try alias
                      const aliasBook = REVERSE_ALIASES[book];
                      if (aliasBook) {
                        const aliasKey = `spreads_${sign}_${side}_${aliasBook}`;
                        value = record[aliasKey];
                        isMain = record[`${aliasKey}_isMain`] === true;
                        isAlt = record[`${aliasKey}_isAlternate`] === true;
                        isAvailable = record[`${aliasKey}_isAvailable`] !== false;
                      }
                    }

                    if (value !== undefined) {
                      return { odds: value, isMain, isAlt, isAvailable };
                    }
                    return null;
                  };

                  // Check which combinations have data
                  const rows = [];
                  let hasAnyEdge = false; // Track if any combination has a single-book edge

                  for (const combo of spreadCombinations) {
                    // Count how many books have odds for this combo
                    const booksWithOdds = bookmakers.filter(book => {
                      const result = getSpreadOddsForBook(book, combo.sign, combo.side);
                      return result && result.odds !== undefined;
                    });

                    if (booksWithOdds.length > 0) {
                      // Skip this row if showSingleBooks is off and only 1 book has odds
                      if (!showSingleBooks && booksWithOdds.length === 1) continue;

                      // Check if this combo has any main lines (for showAlts filter)
                      const hasMainLine = bookmakers.some(book => {
                        const result = getSpreadOddsForBook(book, combo.sign, combo.side);
                        return result?.isMain === true;
                      });

                      // Skip this row if showAlts is off and no main lines exist
                      if (!showAlts && !hasMainLine) continue;

                      // Check if this combination has a single-book edge (excluding unavailable)
                      const oddsWithAvailability = booksWithOdds.map(book => {
                        const result = getSpreadOddsForBook(book, combo.sign, combo.side);
                        return { odds: result?.odds, isAvailable: result?.isAvailable !== false };
                      }).filter(o => o.odds !== undefined && o.odds !== null);

                      let comboHasEdge = false;
                      if (oddsWithAvailability.length > 1) {
                        const bestOdds = Math.max(...oddsWithAvailability.map(o => o.odds));
                        const booksWithBest = oddsWithAvailability.filter(o => o.odds === bestOdds);
                        // Only count as edge if exactly 1 book has best AND it's available
                        if (booksWithBest.length === 1 && booksWithBest[0].isAvailable) {
                          comboHasEdge = true;
                          hasAnyEdge = true;
                        }
                      }

                      // If showEdgesOnly, skip combos without edges
                      if (showEdgesOnly && !comboHasEdge) continue;

                      rows.push(combo);
                    }
                  }

                  // Skip if no rows to render
                  if (rows.length === 0) return null;

                  // Check if we need to insert a header row BEFORE this point group
                  const needsHeader = rowCount > 0 && rowCount % HEADER_INTERVAL < rows.length && Math.floor(rowCount / HEADER_INTERVAL) > Math.floor((rowCount - 1) / HEADER_INTERVAL) === false && rowCount >= HEADER_INTERVAL;
                  // Simpler check: insert header before this group if we've passed a multiple of HEADER_INTERVAL
                  const shouldInsertHeader = rowCount > 0 && Math.floor((rowCount + rows.length - 1) / HEADER_INTERVAL) > Math.floor((rowCount - 1) / HEADER_INTERVAL);

                  // Update row count for this group
                  const groupStartRow = rowCount;
                  rowCount += rows.length;

                  // Render rows for this point value with rowSpan on point column
                  const dataRows = rows.map((combo, comboIdx) => {
                    const allOddsForCombo = bookmakers.map(book => {
                      const result = getSpreadOddsForBook(book, combo.sign, combo.side);
                      return result?.odds;
                    }).filter(o => o !== undefined && o !== null);

                    return (
                      <tr key={`${point}-${combo.sign}-${combo.side}`}>
                        {comboIdx === 0 && (
                          <td
                            rowSpan={rows.length}
                            style={{
                              verticalAlign: 'middle',
                              fontWeight: 'bold',
                              backgroundColor: 'rgba(0,0,0,0.02)'
                            }}
                          >
                            {pointDisplay}
                          </td>
                        )}
                        <td style={{
                          fontWeight: 'bold',
                          fontSize: '1rem',
                          color: combo.displaySign === '+' ? '#10B981' : '#EF4444'
                        }}>
                          {combo.displaySign}
                        </td>
                        <td style={{ fontWeight: '500' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                            <img
                              src={combo.side === 'away' ? (game?.awayLogo || getTeamLogoUrl(game?.away)) : (game?.homeLogo || getTeamLogoUrl(game?.home))}
                              alt={combo.label}
                              style={{ width: '16px', height: '16px' }}
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                            <span style={{ fontSize: '0.75rem' }}>{combo.label}</span>
                          </div>
                        </td>
                        {bookmakers.map(book => {
                          const result = getSpreadOddsForBook(book, combo.sign, combo.side);
                          const odds = result?.odds;
                          const isAvailable = result?.isAvailable !== false;
                          // Get bestBook and bestBookDiff for this spread combination
                          const bestBook = record[`spreads_bestBook_${combo.sign}_${combo.side}`];
                          const bestBookDiff = record[`spreads_bestBookDiff_${combo.sign}_${combo.side}`];
                          // Build allOddsWithBooks for next best book logic
                          const allOddsWithBooks = bookmakers.map(b => {
                            const r = getSpreadOddsForBook(b, combo.sign, combo.side);
                            return { book: b, odds: r?.odds };
                          }).filter(o => o.odds !== undefined && o.odds !== null);
                          const cellStyle = getCellStyle(odds, allOddsForCombo, bestBookDiff, bestBook, book, allOddsWithBooks);

                          return (
                            <td
                              key={book}
                              style={{ padding: '4px', opacity: !isAvailable ? 0.25 : undefined, cursor: odds ? 'pointer' : 'default' }}
                              onClick={(e) => isMobile && odds && handleOddsHover(e, odds, book, bestBook, bestBookDiff)}
                              onMouseEnter={(e) => !isMobile && odds && handleOddsHover(e, odds, book, bestBook, bestBookDiff)}
                              onMouseLeave={() => !isMobile && hideTooltip()}
                            >
                              {odds !== undefined && odds !== null ? (
                                <div style={{ ...cellStyle, padding: '2px 4px' }}>
                                  {formatOdds(odds)}
                                </div>
                              ) : (
                                <span style={{ color: '#ccc' }}>-</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  });

                  // Insert header before this group if needed
                  return shouldInsertHeader && groupStartRow >= HEADER_INTERVAL
                    ? [renderHeaderRow(`spreads-${groupStartRow}`), ...dataRows]
                    : dataRows;
                }

                // For h2h and totals, use the standard sides approach
                // Helper to get odds with alias fallback
                // Handles both patterns: {market}_{book}_{side} and {market}_{side}_{book}
                const getOddsForBook = (book, side) => {
                  // Pattern 1: marketType_book_side (e.g., h2h_fanduel_home)
                  let value = record[`${marketType}_${book}_${side}`];
                  if (value !== undefined) return value;
                  // Pattern 2: marketType_side_book (e.g., totals_over_fanduel)
                  value = record[`${marketType}_${side}_${book}`];
                  if (value !== undefined) return value;
                  // Try reverse alias with pattern 1
                  const aliasBook = REVERSE_ALIASES[book];
                  if (aliasBook) {
                    value = record[`${marketType}_${aliasBook}_${side}`];
                    if (value !== undefined) return value;
                    // Try alias with pattern 2
                    value = record[`${marketType}_${side}_${aliasBook}`];
                    if (value !== undefined) return value;
                  }
                  return undefined;
                };

                // Helper to check availability for a book/side combination
                const getIsAvailable = (book, side) => {
                  // Check both patterns for availability flag
                  const key1 = `${marketType}_${book}_${side}_isAvailable`;
                  const key2 = `${marketType}_${side}_${book}_isAvailable`;
                  if (record[key1] === false || record[key2] === false) return false;
                  // Try alias
                  const aliasBook = REVERSE_ALIASES[book];
                  if (aliasBook) {
                    const aliasKey1 = `${marketType}_${aliasBook}_${side}_isAvailable`;
                    const aliasKey2 = `${marketType}_${side}_${aliasBook}_isAvailable`;
                    if (record[aliasKey1] === false || record[aliasKey2] === false) return false;
                  }
                  return true;
                };

                // For totals, check if this point value should be hidden (only 1 book has data)
                if (!showSingleBooks && marketType === 'totals') {
                  // Count books that have data for either over or under at this point
                  const booksWithData = bookmakers.filter(book =>
                    getOddsForBook(book, 'over') !== undefined ||
                    getOddsForBook(book, 'under') !== undefined
                  );
                  if (booksWithData.length <= 1) return null;
                }

                // Filter sides that have edges if showEdgesOnly is on (only for spreads/totals, not h2h)
                const filteredSides = (showEdgesOnly && marketType !== 'h2h') ? sides.filter(side => {
                  const oddsWithAvailability = bookmakers.map(book => ({
                    odds: getOddsForBook(book, side),
                    isAvailable: getIsAvailable(book, side)
                  })).filter(o => o.odds !== undefined && o.odds !== null);

                  if (oddsWithAvailability.length > 1) {
                    const bestOdds = Math.max(...oddsWithAvailability.map(o => o.odds));
                    const booksWithBest = oddsWithAvailability.filter(o => o.odds === bestOdds);
                    // Only include if exactly 1 book has best AND it's available
                    return booksWithBest.length === 1 && booksWithBest[0].isAvailable;
                  }
                  return false;
                }) : sides;

                // Skip this point if no sides pass the filter
                if (filteredSides.length === 0) return null;

                // Check if we need to insert a header row BEFORE this point group
                const shouldInsertHeader = rowCount > 0 && Math.floor((rowCount + filteredSides.length - 1) / HEADER_INTERVAL) > Math.floor((rowCount - 1) / HEADER_INTERVAL);

                // Update row count for this group
                const groupStartRow = rowCount;
                rowCount += filteredSides.length;

                // Render rows for this point value with rowSpan on point column
                const dataRows = filteredSides.map((side, sideIdx) => {
                  // Collect all odds for this side to determine best
                  const allOddsForSide = bookmakers.map(book => getOddsForBook(book, side))
                    .filter(o => o !== undefined && o !== null);

                  return (
                    <tr key={`${point}-${side}`}>
                      {sideIdx === 0 && (
                        <td
                          rowSpan={filteredSides.length}
                          style={{
                            verticalAlign: 'middle',
                            fontWeight: 'bold',
                            backgroundColor: 'rgba(0,0,0,0.02)'
                          }}
                        >
                          {pointDisplay}
                        </td>
                      )}
                      <td style={{ fontWeight: '500' }}>
                        {marketType === 'totals' ? (
                          <span style={{ color: side === 'over' ? '#10B981' : '#EF4444' }}>
                            {sideLabels[side]}
                          </span>
                        ) : side === 'draw' ? (
                          <span style={{ fontSize: '0.75rem' }}>Draw</span>
                        ) : (
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                            <img
                              src={side === 'away' ? (game?.awayLogo || getTeamLogoUrl(game?.away)) : (game?.homeLogo || getTeamLogoUrl(game?.home))}
                              alt={sideLabels[side]}
                              style={{ width: '16px', height: '16px' }}
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                            <span style={{ fontSize: '0.75rem' }}>{sideLabels[side]}</span>
                          </div>
                        )}
                      </td>
                      {bookmakers.map(book => {
                        const odds = getOddsForBook(book, side);
                        const isAvailable = getIsAvailable(book, side);
                        // Get bestBook and bestBookDiff for this market/side combination
                        const bestBook = record[`${marketType}_bestBook_${side}`];
                        const bestBookDiff = record[`${marketType}_bestBookDiff_${side}`];
                        // Build allOddsWithBooks for next best book logic
                        const allOddsWithBooks = bookmakers.map(b => ({
                          book: b,
                          odds: getOddsForBook(b, side)
                        })).filter(o => o.odds !== undefined && o.odds !== null);
                        const cellStyle = getCellStyle(odds, allOddsForSide, bestBookDiff, bestBook, book, allOddsWithBooks);

                        // Build betMeta for the betslip. Soccer 3-way h2h
                        // uses market key "h2h_3_way"; everyone else uses the
                        // raw marketType. game-level legs have null playerId
                        // and carry the display label in `name`.
                        const resolvedMarketKey = (marketType === 'h2h' && sport === 'soccer') ? 'h2h_3_way' : marketType;
                        const linePoint = (point === 'main' || marketType === 'h2h') ? null : parseFloat(point);
                        const betMeta = {
                          gameId,
                          playerId: null,
                          playerName: sideLabels[side],
                          marketKey: resolvedMarketKey,
                          awayTeam: game?.away,
                          homeTeam: game?.home,
                          playerTeam: null,
                          playerPosition: null,
                          line: linePoint,
                          side,
                          pointLabel: marketType === 'totals' ? `${sideLabels[side]} ${point}` : sideLabels[side],
                        };

                        return (
                          <td
                            key={book}
                            style={{ padding: '4px', opacity: !isAvailable ? 0.25 : undefined, cursor: odds ? 'pointer' : 'default' }}
                            onClick={(e) => odds && activateCell(e, { odds, book, bestBook, bestBookDiff, betMeta })}
                            onMouseEnter={(e) => !isMobile && odds && handleOddsHover(e, odds, book, bestBook, bestBookDiff)}
                            onMouseLeave={() => !isMobile && hideTooltip()}
                          >
                            {odds !== undefined && odds !== null ? (
                              <div style={{ ...cellStyle, padding: '2px 4px' }}>
                                {formatOdds(odds)}
                              </div>
                            ) : (
                              <span style={{ color: '#ccc' }}>-</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                });

                // Insert header before this group if needed
                return shouldInsertHeader && groupStartRow >= HEADER_INTERVAL
                  ? [renderHeaderRow(`${marketType}-${groupStartRow}`), ...dataRows]
                  : dataRows;
              })}
            </tbody>
          </Table>
        </div>
      </div>
    );
  };

  // =============================================================================
  // PERFORMANCE OPTIMIZATION: Memoize processed market data
  // Only recomputes when marketData, showAlts, or sport changes
  // To revert: change back to `const markets = processMarketData();`
  // =============================================================================
  const markets = useMemo(() => processMarketData(), [marketData, showAlts, sport]);

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      centered
      scrollable
      fullscreen="sm-down"
      dialogClassName="h2h-game-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title style={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <img
              src={game?.awayLogo || getTeamLogoUrl(game?.away)}
              alt={game?.away}
              style={{ width: '32px', height: '32px' }}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <span>{game?.away || 'Away'}</span>
          </div>
          <span style={{ color: '#6c757d', fontSize: '0.9rem' }}>@</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <img
              src={game?.homeLogo || getTeamLogoUrl(game?.home)}
              alt={game?.home}
              style={{ width: '32px', height: '32px' }}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <span>{game?.home || 'Home'}</span>
          </div>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {loading && (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2 text-muted">Loading odds...</p>
          </div>
        )}

        {error && (
          <div className="text-center py-4 text-danger">
            <p>Error loading data: {error}</p>
          </div>
        )}

        {!loading && !error && marketData && (
          <>
            {/* Moneyline - Always visible */}
            {renderMarketTable(markets.h2h, 'h2h', 'Moneyline')}

            {/* Toggles for Spreads and Totals - mutually exclusive, plus Hide Alts */}
            <div className="d-flex justify-content-center align-items-center gap-2 mb-3">
              <Button
                variant={showSpreads ? 'primary' : 'outline-primary'}
                size="sm"
                onClick={() => { setShowSpreads(true); setShowTotals(false); }}
              >
                Spreads
              </Button>
              <Button
                variant={showTotals ? 'primary' : 'outline-primary'}
                size="sm"
                onClick={() => { setShowTotals(true); setShowSpreads(false); }}
              >
                Totals
              </Button>
              <span style={{ borderLeft: '1px solid #6c757d', height: '24px', margin: '0 8px' }} />
              <Button
                variant={showAlts ? 'warning' : 'outline-secondary'}
                size="sm"
                onClick={() => setShowAlts(!showAlts)}
                title="Show alternate lines in addition to main lines"
              >
                Show Alts
              </Button>
              <Button
                variant={showSingleBooks ? 'warning' : 'outline-secondary'}
                size="sm"
                onClick={() => setShowSingleBooks(!showSingleBooks)}
                title="Include lines where only 1 book has odds"
              >
                Show Single Books
              </Button>
              <Button
                variant={showEdgesOnly ? 'warning' : 'outline-secondary'}
                size="sm"
                onClick={() => setShowEdgesOnly(!showEdgesOnly)}
                title="Show all lines where there is 1 book with an edge"
              >
                Show Edges
              </Button>
            </div>

            {/* Spreads - Togglable */}
            {showSpreads && renderMarketTable(markets.spreads, 'spreads', 'Spreads')}

            {/* Totals - Togglable */}
            {showTotals && renderMarketTable(markets.totals, 'totals', 'Totals')}
          </>
        )}

        {!loading && !error && !marketData && (
          <div className="text-center py-4 text-muted">
            <p>No odds data available for this game.</p>
          </div>
        )}
      </Modal.Body>

      {/* Edge % Tooltip - ref-based for performance (no re-renders on hover) */}
      <div
        ref={tooltipRef}
        style={{
          display: 'none',
          position: 'fixed',
          backgroundColor: '#1f2937',
          color: 'white',
          padding: '12px 16px',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          zIndex: 9999,
          fontSize: '0.85rem',
          minWidth: '150px',
          pointerEvents: 'none'
        }}
      />

      {/* Mobile bottom-sheet with Add/Remove to Betslip. Only renders when a
          caller wires onOddsClick + betslipItems, so legacy call sites keep
          their existing hover-tooltip behavior unchanged. */}
      {mobileSheet && isMobile && onOddsClick && (
        <div
          role="dialog"
          aria-label="Odds detail"
          style={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: '#1f2937',
            color: 'white',
            padding: '14px 16px calc(16px + env(safe-area-inset-bottom))',
            boxShadow: '0 -6px 20px rgba(0,0,0,0.5)',
            borderTop: '1px solid #374151',
            borderTopLeftRadius: '14px',
            borderTopRightRadius: '14px',
            zIndex: 1210,
            fontSize: '0.9rem',
            animation: 'toastFadeUp 0.2s ease-out',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
            <span style={{ fontWeight: 700, fontSize: '0.95rem' }}>
              {mobileSheet.book.charAt(0).toUpperCase() + mobileSheet.book.slice(1)} ({mobileSheet.point})
            </span>
            <button
              type="button"
              onClick={() => setMobileSheet(null)}
              aria-label="Close"
              style={{ background: 'transparent', border: 'none', color: '#d1d5db', fontSize: '1.4rem', lineHeight: 1, padding: '4px 10px', cursor: 'pointer' }}
            >
              ×
            </button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
            <div>
              <div style={{ color: '#9ca3af', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Odds</div>
              <div style={{ fontWeight: 700, fontSize: '1.05rem' }}>{mobileSheet.odds > 0 ? `+${mobileSheet.odds}` : mobileSheet.odds}</div>
            </div>
            <div>
              <div style={{ color: '#9ca3af', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Implied</div>
              <div style={{ fontWeight: 700, fontSize: '1.05rem', color: '#60a5fa' }}>{mobileSheet.impliedProb}%</div>
            </div>
            <div>
              <div style={{ color: '#9ca3af', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Edge</div>
              <div style={{ fontWeight: 700, fontSize: '1.05rem', color: parseFloat(mobileSheet.edge) > 0 ? '#34d399' : '#f87171' }}>
                {parseFloat(mobileSheet.edge) > 0 ? '+' : ''}{mobileSheet.edge}%
              </div>
            </div>
          </div>
          {(() => {
            const m = mobileSheet.meta;
            if (!m) return null;
            const normLine = (v) => {
              if (v == null || v === '') return v;
              const n = parseFloat(v);
              return Number.isFinite(n) ? n : v;
            };
            const targetLine = normLine(m.line);
            // Game-level legs: match by gameId + market + side + line.
            // Both sides have null playerId, so matching on playerId is a no-op.
            const existing = betslipItems.find(bet =>
              bet.gameId === m.gameId &&
              bet.playerId == null &&
              bet.market === m.marketKey &&
              normLine(bet.line) === targetLine &&
              bet.side === m.side
            );
            const btnBase = { width: '100%', marginTop: '12px', padding: '12px 14px', fontSize: '0.95rem', fontWeight: 700, border: 'none', borderRadius: '10px', cursor: 'pointer', color: '#fff' };
            if (existing) {
              return (
                <button
                  type="button"
                  style={{ ...btnBase, backgroundColor: '#dc2626' }}
                  onClick={() => { if (onRemoveBet) onRemoveBet(existing.id); setMobileSheet(null); }}
                >
                  Remove from Betslip
                </button>
              );
            }
            return (
              <button
                type="button"
                style={{ ...btnBase, backgroundColor: '#10b981' }}
                onClick={() => {
                  if (onOddsClick) {
                    onOddsClick(
                      m.gameId, m.playerId, m.playerName, m.marketKey,
                      mobileSheet.book, mobileSheet.odds, m.awayTeam, m.homeTeam,
                      m.playerTeam, m.playerPosition, m.line, m.side
                    );
                  }
                  setMobileSheet(null);
                }}
              >
                Add to Betslip
              </button>
            );
          })()}
        </div>
      )}
    </Modal>
  );
};

// =============================================================================
// PERFORMANCE OPTIMIZATION: React.memo prevents re-renders when props unchanged
// To revert: change to `export default H2HGameModal;`
// =============================================================================
export default React.memo(H2HGameModal, (prevProps, nextProps) => {
  // Return true if props are equal (skip re-render)
  // Only re-render when these key props change
  if (prevProps.show !== nextProps.show) return false;
  if (prevProps.gameId !== nextProps.gameId) return false;
  if (prevProps.sport !== nextProps.sport) return false;
  if (prevProps.bookmakers !== nextProps.bookmakers) return false;
  // Betslip identity: re-render so the mobile sheet's Add/Remove button
  // reflects the current list.
  if (prevProps.betslipItems !== nextProps.betslipItems) return false;
  if (prevProps.onOddsClick !== nextProps.onOddsClick) return false;
  if (prevProps.onRemoveBet !== nextProps.onRemoveBet) return false;
  // Props are equal, skip re-render
  return true;
});
